import { useState, useEffect, useRef } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { toast } from "sonner";
import {
  RefreshCw,
  Loader2,
  Play,
  Package,
  ClipboardList,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Zap,
  PauseCircle,
} from "lucide-react";

/**
 * AdminOdooLiveMonitor · Feb-2026
 * Monitorea el pipeline de live-sync Odoo → Postgres.
 * Muestra estado del kill-switch, intervalos, últimas corridas de orders/inventory
 * y permite disparar polls manuales (útil especialmente en preview donde
 * ODOO_LIVE_SYNC_ENABLED=false por defecto).
 */
export default function AdminOdooLiveMonitor() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pollingOrders, setPollingOrders] = useState(false);
  const [pollingInventory, setPollingInventory] = useState(false);
  const [orderBatches, setOrderBatches] = useState(3);
  const pollingRef = useRef(null);

  const load = async () => {
    try {
      const { data: res } = await api.get("/admin/odoo-live/status");
      setData(res);
    } catch (e) {
      toast.error("Error cargando status: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    return () => pollingRef.current && clearInterval(pollingRef.current);
  }, []);

  // Auto-refresh cada 3s mientras algún job esté "running"
  useEffect(() => {
    const anyRunning =
      data?.last_run?.orders?.status === "running" ||
      data?.last_run?.inventory?.status === "running";
    if (anyRunning && !pollingRef.current) {
      pollingRef.current = setInterval(load, 3000);
    } else if (!anyRunning && pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, [data]);

  const runOrders = async () => {
    setPollingOrders(true);
    try {
      const { data: res } = await api.post(`/admin/odoo-live/poll-orders?max_batches=${orderBatches}`);
      toast.success(`Poll de órdenes iniciado (max_batches=${orderBatches})`);
      await load();
    } catch (e) {
      toast.error("Error: " + (e?.response?.data?.detail || e.message));
    } finally {
      setPollingOrders(false);
    }
  };

  const runInventory = async () => {
    setPollingInventory(true);
    try {
      await api.post("/admin/odoo-live/poll-inventory");
      toast.success("Poll de inventario iniciado");
      await load();
    } catch (e) {
      toast.error("Error: " + (e?.response?.data?.detail || e.message));
    } finally {
      setPollingInventory(false);
    }
  };

  if (loading || !data) {
    return (
      <Layout>
        <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-gray-400" /></div>
      </Layout>
    );
  }

  const o = data.last_run.orders;
  const inv = data.last_run.inventory;

  return (
    <Layout>
      <div className="space-y-6" data-testid="admin-odoo-live-monitor-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div
              className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full mb-2"
              style={{ background: "#FBFCE0", border: "1px solid #E9EE6E" }}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#C4CA12" }} />
              <span className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: "#6B7000" }}>
                Odoo Live-Sync · Monitoreo
              </span>
            </div>
            <h1 className="text-3xl font-bold tracking-tight" style={{ color: "#0A3747" }}>
              Sincronización con Odoo
            </h1>
            <p className="mt-1 text-sm" style={{ color: "#64748B" }}>
              Pipeline read-only: JOIN de 8 tablas en Odoo → <code>master_ot</code> y <code>master_inventory</code>.
            </p>
          </div>
          <button onClick={load} className="neu-btn neu-btn-primary" data-testid="odoo-live-refresh">
            <RefreshCw size={14} /> Refrescar
          </button>
        </div>

        {/* Kill switch status */}
        <div
          className={`neu-card p-5 flex items-center gap-4 ${data.enabled ? "" : "border-l-4"}`}
          style={data.enabled ? {} : { borderLeftColor: "#F59E0B" }}
        >
          {data.enabled ? (
            <>
              <div className="rounded-full p-3" style={{ background: "#D1FAE5" }}>
                <Zap size={22} className="text-emerald-700" />
              </div>
              <div className="flex-1">
                <div className="font-bold text-emerald-800">Loop automático ACTIVO</div>
                <div className="text-xs text-gray-600 mt-0.5">
                  Órdenes cada <b>{data.interval_sec}s</b> · Inventario cada <b>{Math.round(data.inventory_interval_sec / 3600)}h</b> · Batch orders <b>{data.orders_batch_size}</b>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="rounded-full p-3" style={{ background: "#FEF3C7" }}>
                <PauseCircle size={22} className="text-amber-700" />
              </div>
              <div className="flex-1">
                <div className="font-bold text-amber-800">Loop automático PAUSADO</div>
                <div className="text-xs text-gray-600 mt-0.5">
                  <code>ODOO_LIVE_SYNC_ENABLED=false</code>. Los loops no corren automáticamente.
                  <b> Usa los botones de abajo para disparar polls manuales.</b>
                </div>
              </div>
            </>
          )}
        </div>

        {/* v3.9.8 · Política incremental (write_date HW + blacklist) */}
        {data.incremental_policy && (
          <div className="neu-card p-4" data-testid="incremental-policy-panel">
            <div className="flex items-start gap-3 flex-wrap">
              <div className="rounded-full p-2 flex-shrink-0" style={{ background: "#DBEAFE" }}>
                <Zap size={16} className="text-blue-700" />
              </div>
              <div className="flex-1 min-w-[280px]">
                <div className="text-[10px] font-mono uppercase tracking-widest font-bold text-blue-800 mb-1">
                  Política incremental estricta
                </div>
                <div className="text-xs text-gray-700 mb-2">
                  Sólo se traen OTs con <code className="px-1 py-0.5 rounded bg-gray-100">write_date &gt; HW</code>{" "}
                  ({data.last_run?.orders?.high_water_mark
                    ? new Date(data.last_run.orders.high_water_mark).toLocaleString("es-EC")
                    : "sin HW aún"}) y que NO estén en estados terminales.
                  El HW ignora OTs terminales para no perder OTs activas más antiguas.
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-800 mb-1">
                      Whitelist (allowed)
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {(data.incremental_policy.allowed_stage_names || []).length === 0 ? (
                        <span className="text-[10px] text-gray-400 italic">(sin whitelist · trae todos los stages no excluidos)</span>
                      ) : (
                        data.incremental_policy.allowed_stage_names.map((s) => (
                          <span key={s} className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-200 font-mono">
                            {s}
                          </span>
                        ))
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-red-800 mb-1">
                      Blacklist (excluded)
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {(data.incremental_policy.excluded_stage_names || []).map((s) => (
                        <span key={s} className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-800 border border-red-200 font-mono">
                          {s}
                        </span>
                      ))}
                      {(data.incremental_policy.excluded_stage_ids || []).map((id) => (
                        <span key={`id-${id}`} className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-800 border border-red-200 font-mono">
                          id={id}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Cards de acción */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Órdenes */}
          <div className="neu-card p-5 space-y-4" data-testid="odoo-live-orders-card">
            <div className="flex items-center gap-2">
              <ClipboardList size={20} style={{ color: "#0A3747" }} />
              <h2 className="text-lg font-bold" style={{ color: "#0A3747" }}>Órdenes de trabajo</h2>
              <StatusChip status={o.status} />
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <Metric label="Filas obtenidas" value={o.rows_fetched?.toLocaleString("es-EC") ?? "—"} />
              <Metric label="Filas upserteadas" value={o.rows_upserted?.toLocaleString("es-EC") ?? "—"} />
              <Metric label="Duración" value={o.duration_sec != null ? `${o.duration_sec}s` : "—"} />
              <Metric label="Última corrida" value={o.finished_at ? new Date(o.finished_at).toLocaleString("es-EC", { timeZone: "America/Guayaquil", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"} />
            </div>

            <div className="text-[11px] bg-gray-50 rounded-md px-3 py-2">
              <span className="text-gray-500 uppercase font-semibold tracking-wider">High water mark:</span>
              <div className="font-mono text-xs mt-0.5">{o.high_water_mark || "— (primera corrida)"}</div>
            </div>

            {o.error && (
              <div className="bg-red-50 border border-red-200 rounded-md px-3 py-2 text-xs text-red-800 flex items-start gap-2">
                <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                <div><b>Error:</b> {o.error.slice(0, 300)}</div>
              </div>
            )}

            <div className="flex items-center gap-2 pt-2 border-t border-gray-200">
              <label className="text-xs text-gray-600 flex items-center gap-2">
                Batches:
                <input
                  type="number"
                  value={orderBatches}
                  onChange={(e) => setOrderBatches(Math.max(1, Math.min(50, Number(e.target.value) || 1)))}
                  min={1}
                  max={50}
                  className="w-16 px-2 py-1 border border-gray-300 rounded text-center tabular-nums"
                  data-testid="odoo-live-orders-batches"
                />
                <span className="text-[11px] text-gray-400">(× 500 filas)</span>
              </label>
              <button
                onClick={runOrders}
                disabled={pollingOrders || o.status === "running"}
                className="neu-btn neu-btn-primary ml-auto"
                data-testid="odoo-live-poll-orders-btn"
              >
                {pollingOrders || o.status === "running" ? (
                  <><Loader2 size={14} className="animate-spin" /> {o.status === "running" ? "Corriendo…" : "Iniciando…"}</>
                ) : (
                  <><Play size={14} /> Poll manual</>
                )}
              </button>
            </div>
          </div>

          {/* Inventario */}
          <div className="neu-card p-5 space-y-4" data-testid="odoo-live-inventory-card">
            <div className="flex items-center gap-2">
              <Package size={20} style={{ color: "#0A3747" }} />
              <h2 className="text-lg font-bold" style={{ color: "#0A3747" }}>Inventario</h2>
              <StatusChip status={inv.status} />
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <Metric label="Filas upserteadas" value={inv.rows_upserted?.toLocaleString("es-EC") ?? "—"} />
              <Metric label="Duración" value={inv.duration_sec != null ? `${inv.duration_sec}s` : "—"} />
              <Metric label="Última corrida" value={inv.finished_at ? new Date(inv.finished_at).toLocaleString("es-EC", { timeZone: "America/Guayaquil", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"} />
              <Metric label="Estado" value={inv.status} />
            </div>

            {inv.error && (
              <div className="bg-red-50 border border-red-200 rounded-md px-3 py-2 text-xs text-red-800 flex items-start gap-2">
                <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                <div><b>Error:</b> {inv.error.slice(0, 300)}</div>
              </div>
            )}

            <div className="flex items-center justify-end pt-2 border-t border-gray-200">
              <button
                onClick={runInventory}
                disabled={pollingInventory || inv.status === "running"}
                className="neu-btn neu-btn-primary"
                data-testid="odoo-live-poll-inventory-btn"
              >
                {pollingInventory || inv.status === "running" ? (
                  <><Loader2 size={14} className="animate-spin" /> {inv.status === "running" ? "Corriendo…" : "Iniciando…"}</>
                ) : (
                  <><Play size={14} /> Poll manual</>
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="text-[11px] text-gray-500 text-center">
          Endpoint: <code>GET /api/admin/odoo-live/status</code>.
          Polls: <code>POST /api/admin/odoo-live/poll-orders</code> / <code>/poll-inventory</code>.
          Refresco automático cada 3s mientras un job esté <code>running</code>.
        </div>
      </div>
    </Layout>
  );
}

function Metric({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">{label}</div>
      <div className="text-base font-bold tabular-nums" style={{ color: "#0A3747" }}>{value}</div>
    </div>
  );
}

function StatusChip({ status }) {
  const styles = {
    ok: { bg: "bg-emerald-100", fg: "text-emerald-800", icon: CheckCircle2, label: "OK" },
    running: { bg: "bg-blue-100", fg: "text-blue-800", icon: Loader2, label: "Corriendo…", spin: true },
    error: { bg: "bg-red-100", fg: "text-red-800", icon: AlertTriangle, label: "Error" },
    idle: { bg: "bg-gray-100", fg: "text-gray-700", icon: Clock, label: "Sin correr" },
  };
  const st = styles[status] || styles.idle;
  const Icon = st.icon;
  return (
    <span className={`ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${st.bg} ${st.fg}`}>
      <Icon size={11} className={st.spin ? "animate-spin" : ""} />
      {st.label}
    </span>
  );
}
